import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useWaitTimeManager, useWaitTimes } from '../../hooks/useWaitTimeManager';
import { Box, Typography, Button, Paper, Divider } from '@mui/material';
import waitTimeManager from '../../services/WaitTimeManager';

const WaitTimeDebug = () => {
  const { startManager, stopManager, isActive, hasData, dataCount } = useWaitTimeManager();
  const { waitTimes, nextUnavailable, isCalculating } = useWaitTimes();
  
  // Redux Data
  const shiftsData = useSelector(state => state.shifts.data);
  const vehicles = useSelector(state => state.vehicles.vehicles);
  const activities = useSelector(state => state.rides.activities);
  
  // WaitTimeManager interne Daten
  const [managerStatus, setManagerStatus] = useState(null);
  
  // Manager Status regelmäßig aktualisieren
  useEffect(() => {
    const updateStatus = () => {
      const status = waitTimeManager.getStatus();
      setManagerStatus(status);
    };
    
    // Initial
    updateStatus();
    
    // Alle 5 Sekunden aktualisieren um Performance zu verbessern
    const interval = setInterval(updateStatus, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const handleStartManager = async () => {
    try {
      await startManager();
    } catch (error) {
      console.error('Debug: Fehler beim Start:', error);
    }
  };

  const handleStopManager = async () => {
    try {
      await stopManager();
    } catch (error) {
      console.error('Debug: Fehler beim Stop:', error);
    }
  };

  const handleManualCalculation = async () => {
    try {
      // Daten manuell aktualisieren
      waitTimeManager.updateFromReduxStore(shiftsData, activities, vehicles);
      // Berechnung triggern
      await waitTimeManager.performCalculation();
    } catch (error) {
      console.error('Debug: Fehler bei manueller Berechnung:', error);
    }
  };

  return (
    <Paper sx={{ p: 2, m: 2 }}>
      <Typography variant="h6" gutterBottom>
        WaitTime Debug Panel
      </Typography>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2">Service Status:</Typography>
        <Typography variant="body2">
          • Manager Active: {isActive ? '✅' : '❌'}<br/>
          • Has Data (Hook): {hasData ? '✅' : '❌'}<br/>
          • Has Data (Manager): {managerStatus?.hasData ? '✅' : '❌'}<br/>
          • Is Calculating: {isCalculating ? '⏳' : '✅'}<br/>
          • Drivers (Hook): {dataCount.drivers}<br/>
          • Drivers (Manager): {managerStatus?.dataCount.drivers || 0}<br/>
          • Vehicles: {dataCount.vehicles}<br/>
          • Activities: {dataCount.activities}<br/>
          • Shifts Listener: {managerStatus?.hasShiftsListener ? '✅' : '❌'}
        </Typography>
      </Box>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2">Current Wait Times:</Typography>
        <Typography variant="body2">
          • General: {waitTimes.general}<br/>
          • Großraum: {waitTimes.grossraum}<br/>
          • Kleinbus 7: {waitTimes.kleinbus7}<br/>
          • Kleinbus 8: {waitTimes.kleinbus8}
        </Typography>
      </Box>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2">Redux Data:</Typography>
        <Typography variant="body2">
          • Shifts Data: {shiftsData ? '✅ Available' : '❌ Missing'}<br/>
          • Vehicles: {vehicles?.length || 0}<br/>
          • Activities: {activities?.length || 0}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleStartManager}
          disabled={isActive}
        >
          Start Manager
        </Button>
        <Button 
          variant="contained" 
          color="secondary" 
          onClick={handleStopManager}
          disabled={!isActive}
        >
          Stop Manager
        </Button>
        <Button 
          variant="outlined" 
          onClick={handleManualCalculation}
        >
          Manual Calculation
        </Button>
      </Box>

      {nextUnavailable && (
        <Box sx={{ mt: 2, p: 1, bgcolor: 'warning.light' }}>
          <Typography variant="caption">
            Next Unavailable: {nextUnavailable.minutesUntil} min, Duration: {nextUnavailable.duration} min
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default WaitTimeDebug;